import { Plus, Tag, Truck, X, Package, History, Calendar, Pencil, Check, Euro } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import FornitoreSelect from "../../components/FornitoreSelect";
import { labelUdm, prezzoBaseLabel } from "../../utils/udmUtils";

export const ProdottoDialog = ({ open, onOpenChange, editItem, form, setForm, onSave, saving, isFood, unitaMisura = [], categorie = [] }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border">
      <DialogHeader>
        <DialogTitle>{editItem ? "Modifica Prodotto" : "Nuovo Prodotto"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Nome *</Label>
          <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome prodotto" />
        </div>
        <div>
          <Label>Categoria</Label>
          <Select value={form.categoria_id || "__none__"} onValueChange={(v) => setForm({ ...form, categoria_id: v === "__none__" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nessuna categoria —</SelectItem>
              {categorie.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.icona || (isFood ? "🥗" : "📦")} {c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Unità di misura</Label>
          <Select value={form.unita_misura || (isFood ? "kg" : "pz")} onValueChange={(v) => setForm({ ...form, unita_misura: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {unitaMisura.map(u => (
                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>IVA acquisto (%)</Label>
          <Input
            type="number" step="0.5" min="0"
            value={form.iva_percentuale ?? (isFood ? 10 : 22)}
            onChange={(e) => setForm({ ...form, iva_percentuale: e.target.value })}
            placeholder={isFood ? "10" : "22"}
            data-testid="prodotto-iva-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Usata per calcolare il food cost al lordo IVA (esborso reale). Il prezzo resta netto.</p>
        </div>
        <div>
          <Label>Note</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving}>{saving ? "Salvataggio..." : "Salva"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const CategoriaDialog = ({ open, onOpenChange, editItem, form, setForm, onSave, saving, isFood }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border">
      <DialogHeader>
        <DialogTitle>{editItem ? "Modifica Categoria" : "Nuova Categoria"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Nome *</Label>
          <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome categoria" />
        </div>
        <div>
          <Label>Icona</Label>
          <Input value={form.icona} onChange={(e) => setForm({ ...form, icona: e.target.value })} placeholder={isFood ? "🥗" : "📦"} />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving}>{saving ? "Salvataggio..." : "Salva"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AlertDialog = ({ open, onOpenChange, alertProdotto, form, setForm, onSave, saving }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border">
      <DialogHeader>
        <DialogTitle>Segnala Alert - {alertProdotto?.nome}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Urgenza</Label>
          <Select value={form.urgenza} onValueChange={(v) => setForm({ ...form, urgenza: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in_esaurimento">In esaurimento</SelectItem>
              <SelectItem value="urgente">Urgente</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Note</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">{saving ? "Salvataggio..." : "Crea Alert"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const RiordinoDialog = ({
  open, onOpenChange, form, setForm, onSave, saving, isFood,
  alertDaRisolvere, riordinoProdotto, fornitori, tipologieFornitori,
  fornitoreSearch, setFornitoreSearch, fornitoreDropdownOpen, setFornitoreDropdownOpen,
  onNuovoFornitore
}) => {
  const prodotto = riordinoProdotto || (alertDaRisolvere ? { nome: alertDaRisolvere.materiale_nome || alertDaRisolvere.ingrediente_nome, unita_misura: alertDaRisolvere.unita_misura } : null);
  const udm = prodotto?.unita_misura || (isFood ? "kg" : "pz");
  const udmLabel = labelUdm(udm);
  const prezzoLabel = prezzoBaseLabel(udm);

  return (
  <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setFornitoreDropdownOpen(false); setFornitoreSearch(""); } }}>
    <DialogContent className="bg-card border-border max-w-lg">
      <DialogHeader><DialogTitle>Registra Acquisto</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
        {(alertDaRisolvere || riordinoProdotto) && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
            <p className="font-medium">{prodotto?.nome}</p>
            <span className="text-xs px-2 py-1 rounded-full font-bold" style={{ background: "rgba(108,99,255,0.12)", color: "#6C63FF" }}>{udmLabel}</span>
          </div>
        )}
        <div>
          <Label>Data acquisto *</Label>
          <DateInput value={form.data_acquisto} onChange={(v) => setForm({ ...form, data_acquisto: v })} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Quantità ({udmLabel}) *</Label>
            <Input type="number" value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} placeholder="1000" />
          </div>
          <div>
            <Label>Prezzo totale ({prezzoLabel})*</Label>
            <Input type="number" step="0.0001" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} placeholder="0.00" />
          </div>
        </div>
        {!isFood && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl space-y-3">
            <Label className="flex items-center gap-2 text-amber-700 font-medium">
              <Truck className="h-4 w-4" />
              Costi Extra (trasporto, fustelle, una tantum)
            </Label>
            <Input type="number" step="0.01" value={form.costi_extra || ""} onChange={(e) => setForm({ ...form, costi_extra: e.target.value })} placeholder="€ 0.00" />
            {form.quantita && form.prezzo_unitario && (() => {
              const qty = parseFloat(form.quantita) || 0;
              const prezzoUnit = parseFloat(form.prezzo_unitario) || 0;
              const costiExtra = parseFloat(form.costi_extra) || 0;
              const subtotale = qty * prezzoUnit;
              const totale = subtotale + costiExtra;
              const prezzoEffettivo = qty > 0 ? (totale / qty) : 0;
              return (
                <div className="text-sm space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotale ({qty.toLocaleString()} × €{prezzoUnit.toFixed(4)})</span>
                    <span>€{subtotale.toFixed(2)}</span>
                  </div>
                  {costiExtra > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>+ Costi extra</span>
                      <span>€{costiExtra.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-foreground border-t pt-1">
                    <span>Totale ordine</span>
                    <span>€{totale.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Prezzo unitario effettivo</span>
                    <span>€{prezzoEffettivo.toFixed(4)}/pz</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        <div>
          <Label className="flex items-center justify-between mb-1">
            <span>Fornitore</span>
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNuovoFornitore("riordino"); }} className="text-xs text-primary hover:underline flex items-center gap-1 cursor-pointer">
              <Plus className="h-3 w-3" /> Nuovo
            </span>
          </Label>
          <div className="relative">
            <Input
              placeholder="Cerca fornitore..."
              value={form.fornitore_id ? fornitori.find(f => f.id === form.fornitore_id)?.ragione_sociale || "" : fornitoreSearch}
              onChange={(e) => { setFornitoreSearch(e.target.value); if (form.fornitore_id) setForm({ ...form, fornitore_id: "" }); }}
              onFocus={() => setFornitoreDropdownOpen(true)}
            />
            {form.fornitore_id && (
              <button type="button" onClick={() => setForm({ ...form, fornitore_id: "" })} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
            {fornitoreDropdownOpen && !form.fornitore_id && (
              <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <button type="button" onClick={() => { setForm({ ...form, fornitore_id: "" }); setFornitoreDropdownOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-muted text-sm text-muted-foreground">
                  Nessun fornitore
                </button>
                {Object.entries(
                  fornitori
                    .filter(f => !isFood ? (f.categoria === "packaging" || f.categoria === "servizi") : f.categoria === "food" || !f.categoria)
                    .filter(f => !fornitoreSearch || f.ragione_sociale?.toLowerCase().includes(fornitoreSearch.toLowerCase()))
                    .reduce((acc, f) => {
                      const tip = tipologieFornitori.find(t => t.id === f.tipologia_id)?.nome || "Altro";
                      if (!acc[tip]) acc[tip] = [];
                      acc[tip].push(f);
                      return acc;
                    }, {})
                ).sort().map(([tipologia, forns]) => (
                  <div key={tipologia}>
                    <div className="px-3 py-1 bg-muted text-xs font-semibold text-muted-foreground uppercase sticky top-0 z-10">{tipologia}</div>
                    {forns.map(f => (
                      <button key={f.id} type="button" onClick={() => { setForm({ ...form, fornitore_id: f.id }); setFornitoreDropdownOpen(false); setFornitoreSearch(""); }} className="w-full px-3 py-2 text-left hover:bg-muted text-sm text-foreground">
                        {f.ragione_sociale}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div>
          <Label>Note</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Salvataggio..." : "Conferma"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const CaricoDialog = ({ open, onOpenChange, form, setForm, onSave, saving, isFood, prodotti, fornitori, tipologieFornitori, onNuovoFornitore }) => {
  const prodotto = prodotti?.find(p => p.id === form.prodotto_id);
  const udm = prodotto?.unita_misura || (isFood ? "kg" : "pz");
  const udmLabel = labelUdm(udm);
  const prezzoLabel = prezzoBaseLabel(udm);

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border">
      <DialogHeader><DialogTitle>Inserimento Manuale</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Prodotto *</Label>
          <Select value={form.prodotto_id} onValueChange={(v) => setForm({ ...form, prodotto_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
            <SelectContent>
              {prodotti.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} {p.unita_misura ? <span className="text-xs text-muted-foreground ml-1">({labelUdm(p.unita_misura)})</span> : null}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Quantità ({udmLabel}) *</Label>
          <Input type="number" value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} placeholder="10" />
        </div>
        <div>
          <Label className="mb-2 block">Tipo prezzo</Label>
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => setForm({ ...form, prezzo_tipo: "unitario", prezzo_totale: "" })}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${form.prezzo_tipo === "unitario" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-foreground hover:bg-muted"}`}>
              Prezzo x {udmLabel}
            </button>
            <button type="button" onClick={() => setForm({ ...form, prezzo_tipo: "totale", prezzo_unitario: "" })}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${form.prezzo_tipo === "totale" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-foreground hover:bg-muted"}`}>
              Prezzo Totale
            </button>
          </div>
          {form.prezzo_tipo === "unitario" ? (
            <Input type="number" step="0.01" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} placeholder={`€ per ${udmLabel}`} />
          ) : (
            <Input type="number" step="0.01" value={form.prezzo_totale} onChange={(e) => setForm({ ...form, prezzo_totale: e.target.value })} placeholder="€ totale" />
          )}
          {form.quantita && (form.prezzo_unitario || form.prezzo_totale) && (
            <p className="text-xs text-muted-foreground mt-1">
              Prezzo normalizzato: <span className="font-semibold text-primary">{prezzoLabel}</span>
            </p>
          )}
        </div>
        <div>
          <Label>Data acquisto</Label>
          <DateInput value={form.data_acquisto} onChange={(v) => setForm({ ...form, data_acquisto: v })} />
        </div>
        <div>
          <Label className="flex items-center justify-between mb-1">
            <span>Fornitore</span>
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNuovoFornitore("carico"); }} className="text-xs text-primary hover:underline flex items-center gap-1 cursor-pointer">
              <Plus className="h-3 w-3" /> Nuovo
            </span>
          </Label>
          <FornitoreSelect
            fornitori={fornitori.filter(f => isFood ? (f.categoria === "food" || !f.categoria) : (f.categoria === "packaging" || f.categoria === "servizi"))}
            tipologie={tipologieFornitori}
            value={form.fornitore_id || "__none__"}
            onChange={(v) => setForm({ ...form, fornitore_id: v === "__none__" ? "" : v })}
            placeholder="Seleziona fornitore"
            showNoneOption={true}
            noneOptionLabel="Nessun fornitore"
          />
        </div>
        <div>
          <Label>Note</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Salvataggio..." : "Registra"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const StoricoDialog = ({ open, onOpenChange, storicoProdotto, storicoCarichi, isFood, token, onUpdated }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [editId, setEditId] = useState(null);
  const [editQta, setEditQta] = useState("");
  const [editPrezzoId, setEditPrezzoId] = useState(null);
  const [editPrezzo, setEditPrezzo] = useState("");
  const [saving, setSaving] = useState(false);
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");

  const getDataCarico = (c) => (c.data_riordino || c.data || c.created_at || "").slice(0, 10);
  const carichiFiltrati = (storicoCarichi || []).filter((c) => {
    const d = getDataCarico(c);
    if (dataDa && d < dataDa) return false;
    if (dataA && d > dataA) return false;
    return true;
  });
  const umTot = storicoProdotto?.unita_misura || (isFood ? "kg" : "pz");
  const totQta = carichiFiltrati.reduce((s, c) => s + (Number(c.quantita) || 0), 0);
  const totSpesa = carichiFiltrati.reduce((s, c) => s + (c.prezzo_totale || c.totale || 0), 0);

  const avviaModifica = (c) => {
    setEditPrezzoId(null);
    setEditId(c.id);
    setEditQta(String(c.quantita ?? ""));
  };

  const avviaModificaPrezzo = (c) => {
    setEditId(null);
    setEditPrezzoId(c.id);
    setEditPrezzo(String(c.prezzo_unitario ?? ""));
  };

  const salvaPrezzo = async (c) => {
    const nuovo = parseFloat(String(editPrezzo).replace(",", "."));
    if (!nuovo || nuovo <= 0) { toast.error("Inserisci un prezzo valido (> 0)"); return; }
    setSaving(true);
    try {
      const patchUrl = isFood
        ? `${API}/api/movimenti-magazzino/${c.id}/prezzo-unitario`
        : `${API}/api/materiali/carichi/${c.id}/prezzo-unitario`;
      const res = await axios.patch(
        patchUrl,
        { nuovo_prezzo: nuovo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tot = res.data?.prezzo_totale;
      toast.success(`Prezzo corretto: nuovo totale €${Number(tot).toFixed(2)}`);
      setEditPrezzoId(null);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'aggiornamento del prezzo");
    } finally {
      setSaving(false);
    }
  };

  const salvaQta = async (c) => {
    const nuova = parseFloat(String(editQta).replace(",", "."));
    if (!nuova || nuova <= 0) { toast.error("Inserisci una quantità valida (> 0)"); return; }
    setSaving(true);
    try {
      const patchUrl = isFood
        ? `${API}/api/movimenti-magazzino/${c.id}/quantita`
        : `${API}/api/materiali/carichi/${c.id}/quantita`;
      const res = await axios.patch(
        patchUrl,
        { nuova_quantita: nuova },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pu = res.data?.prezzo_unitario;
      toast.success(`Quantità corretta: nuovo prezzo €${Number(pu).toFixed(4)}/${storicoProdotto?.unita_misura || (isFood ? "kg" : "pz")}`);
      setEditId(null);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il ricalcolo");
    } finally {
      setSaving(false);
    }
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Storico - {storicoProdotto?.nome}
        </DialogTitle>
      </DialogHeader>
      <div className="py-4">
        {!isFood && storicoProdotto?.prezzi_fornitori?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Tag className="h-4 w-4" />Storico Prezzi per Fornitore
            </h4>
            <div className="space-y-2">
              {[...storicoProdotto.prezzi_fornitori].sort((a, b) => (b.data_inserimento || "").localeCompare(a.data_inserimento || "")).slice(0, 5).map((p, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-3">
                    <Truck className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="font-medium text-sm">{p.fornitore_nome}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.data_inserimento ? new Date(p.data_inserimento).toLocaleDateString("it-IT") : "Data n.d."}
                        {p.formato_confezione && ` • ${p.formato_confezione}`}
                        {p.fonte === "fattura" && " • da fattura"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600">€{p.prezzo_unitario?.toFixed(4)}</p>
                    <p className="text-xs text-muted-foreground">/{storicoProdotto.unita_misura || "pz"}</p>
                  </div>
                </div>
              ))}
            </div>
            {storicoProdotto.prezzi_fornitori.length > 5 && (
              <p className="text-xs text-muted-foreground text-center mt-2">+{storicoProdotto.prezzi_fornitori.length - 5} altri prezzi</p>
            )}
          </div>
        )}
        <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
          <Package className="h-4 w-4" />Acquisti Registrati
        </h4>

        {/* Filtro periodo */}
        <div className="mb-4 p-3 bg-muted/30 rounded-xl border border-border" data-testid="storico-filtro-periodo">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[130px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Da</Label>
              <div data-testid="storico-data-da">
                <DateInput value={dataDa} onChange={(v) => setDataDa(v)} />
              </div>
            </div>
            <div className="flex-1 min-w-[130px]">
              <Label className="text-xs text-muted-foreground mb-1 block">A</Label>
              <div data-testid="storico-data-a">
                <DateInput value={dataA} onChange={(v) => setDataA(v)} />
              </div>
            </div>
            {(dataDa || dataA) && (
              <Button variant="ghost" size="sm" className="h-9" onClick={() => { setDataDa(""); setDataA(""); }} data-testid="storico-reset-periodo">
                <X className="h-3.5 w-3.5 mr-1" />Azzera
              </Button>
            )}
          </div>
        </div>

        {carichiFiltrati.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{(dataDa || dataA) ? "Nessun acquisto nel periodo selezionato" : "Nessun acquisto registrato"}</p>
          </div>
        ) : (
          <>
            <div className="mb-4 p-3 bg-primary/5 rounded-xl border border-primary/20" data-testid="storico-riepilogo-periodo">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Numero acquisti:</span>
                <span className="font-semibold" data-testid="storico-tot-acquisti">{carichiFiltrati.length}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-muted-foreground">Totale acquistato:</span>
                <span className="font-semibold text-primary" data-testid="storico-tot-quantita">
                  {totQta.toLocaleString("it-IT", { maximumFractionDigits: 3 })} {umTot}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-muted-foreground">Spesa totale{(dataDa || dataA) ? " del periodo" : ""}:</span>
                <span className="font-semibold text-emerald-500" data-testid="storico-tot-spesa">€{totSpesa.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-3 max-h-[250px] overflow-y-auto">
              {carichiFiltrati.map((c, idx) => {
                const inEditQta = editId === c.id;
                const inEditPrezzo = editPrezzoId === c.id;
                const um = storicoProdotto?.unita_misura || (isFood ? "kg" : "pz");
                const totaleRiga = (c.prezzo_totale || c.totale || (Number(c.prezzo_unitario || 0) * Number(c.quantita || 0)));
                const qtaLive = parseFloat(String(editQta).replace(",", "."));
                const prezzoLive = (inEditQta && qtaLive > 0) ? (totaleRiga / qtaLive) : null;
                const prezzoNuovoLive = parseFloat(String(editPrezzo).replace(",", "."));
                const totaleLive = (inEditPrezzo && prezzoNuovoLive > 0) ? (prezzoNuovoLive * Number(c.quantita || 0)) : null;
                return (
                <div key={c.id || idx} className="p-4 bg-muted/30 rounded-xl border border-border" data-testid={`storico-carico-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="font-medium">{new Date(c.data_riordino || c.data || c.created_at).toLocaleDateString("it-IT")}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <p className="text-muted-foreground flex items-center gap-1">
                          Quantità:
                          {inEditQta ? (
                            <span className="flex items-center gap-1">
                              <Input
                                type="number" step="0.0001" value={editQta}
                                onChange={(e) => setEditQta(e.target.value)}
                                className="h-7 w-24 text-sm"
                                data-testid={`storico-qta-input-${c.id}`}
                                autoFocus
                              />
                              <span className="text-xs">{um}</span>
                            </span>
                          ) : (
                            <span className="text-foreground font-medium">{c.quantita} {um}</span>
                          )}
                        </p>
                        {c.fornitore_nome && <p className="text-muted-foreground flex items-center gap-1"><Truck className="h-3 w-3" />{c.fornitore_nome}</p>}
                        {c.prezzo_unitario != null && (
                          <p className="text-muted-foreground flex items-center gap-1">Prezzo unit.:{" "}
                            {inEditPrezzo ? (
                              <span className="flex items-center gap-1">
                                <Input
                                  type="number" step="0.0001" value={editPrezzo}
                                  onChange={(e) => setEditPrezzo(e.target.value)}
                                  className="h-7 w-24 text-sm"
                                  data-testid={`storico-prezzo-input-${c.id}`}
                                  autoFocus
                                />
                                <span className="text-xs">€/{um}</span>
                              </span>
                            ) : prezzoLive != null ? (
                              <span className="text-primary font-semibold" data-testid={`storico-prezzo-live-${c.id}`}>€{prezzoLive.toFixed(4)}</span>
                            ) : (
                              <span className="text-foreground">€{Number(c.prezzo_unitario).toFixed(4)}</span>
                            )}
                          </p>
                        )}
                        {(c.prezzo_totale || c.totale || totaleLive != null) ? (
                          <p className="text-muted-foreground">Totale:{" "}
                            {totaleLive != null ? (
                              <span className="text-emerald-500 font-semibold" data-testid={`storico-totale-live-${c.id}`}>€{totaleLive.toFixed(2)}</span>
                            ) : (
                              <span className="text-emerald-500 font-medium">€{(c.prezzo_totale || c.totale || 0).toFixed(2)}</span>
                            )}
                          </p>
                        ) : null}
                      </div>
                      {inEditQta && (
                        <p className="text-xs text-muted-foreground mt-1">Il totale resta invariato: il prezzo unitario viene ricalcolato su questa quantità.</p>
                      )}
                      {inEditPrezzo && (
                        <p className="text-xs text-muted-foreground mt-1">La quantità resta invariata: il totale viene ricalcolato (prezzo × quantità).</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {inEditQta ? (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 px-2" disabled={saving} onClick={() => salvaQta(c)} data-testid={`storico-qta-save-${c.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditId(null)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : inEditPrezzo ? (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 px-2" disabled={saving} onClick={() => salvaPrezzo(c)} data-testid={`storico-prezzo-save-${c.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditPrezzoId(null)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => avviaModifica(c)}
                            className="text-indigo-500 hover:text-indigo-700 transition-colors"
                            title="Correggi quantità (totale invariato)"
                            data-testid={`storico-qta-edit-${c.id}`}
                          >
                            <Pencil className="h-4 w-4" strokeWidth={2.25} />
                          </button>
                          <button
                            onClick={() => avviaModificaPrezzo(c)}
                            className="text-emerald-600 hover:text-emerald-700 transition-colors"
                            title="Correggi prezzo unitario (totale ricalcolato)"
                            data-testid={`storico-prezzo-edit-${c.id}`}
                          >
                            <Euro className="h-4 w-4" strokeWidth={2.25} />
                          </button>
                        </div>
                      )}
                      <div className="text-right text-xs text-muted-foreground">
                        <p>Registrato da</p>
                        <p className="font-medium">{c.caricato_da_nome || c.utente_nome}</p>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const DeleteConfirmDialog = ({ deleteId, onClose, onDelete, error = "", deleting = false }) => (
  <Dialog open={!!deleteId} onOpenChange={onClose}>
    <DialogContent className="bg-card border-border" data-testid="delete-confirm-dialog">
      <DialogHeader>
        <DialogTitle data-testid="delete-confirm-title">Conferma eliminazione</DialogTitle>
        <DialogDescription data-testid="delete-confirm-message">
          L'elemento verrà eliminato solo se non è usato in ricette o prodotti menu.
        </DialogDescription>
      </DialogHeader>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700"
          data-testid="delete-dependency-error"
        >
          {error}
        </div>
      )}
      <DialogFooter>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={deleting}
          data-testid="delete-confirm-cancel-button"
        >
          Annulla
        </Button>
        <Button
          onClick={onDelete}
          disabled={deleting}
          className="bg-red-600 hover:bg-red-700"
          data-testid="delete-confirm-submit-button"
        >
          {deleting ? "Verifica in corso..." : "Elimina"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const NuovoFornitoreDialog = ({ open, onOpenChange, form, setForm, onSave, saving, tipologieFornitori }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-border">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />Nuovo Fornitore
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Nome / Ragione Sociale *</Label>
          <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="es. Metro, Coca Cola Italia..." autoFocus />
        </div>
        <div>
          <Label>Categoria *</Label>
          <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="food">Food & Beverage</SelectItem>
              <SelectItem value="packaging">Attrezzature & Packaging</SelectItem>
              <SelectItem value="servizi">Servizi & Utenze</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipologia</Label>
          <Select value={form.tipologia_id} onValueChange={(v) => setForm({ ...form, tipologia_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona tipologia" /></SelectTrigger>
            <SelectContent>
              {tipologieFornitori.map(t => (<SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving || !form.categoria}>{saving ? "Creazione..." : "Crea Fornitore"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
